import React from 'react';
import { useInterviewStore } from '../store';
import { FileTextIcon, ListIcon, CheckSquareIcon } from 'lucide-react';
import { createInterview } from '../services/supabase';
import { generateInterviewQuestion } from '../services/openai';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export function JobDescriptionView() {
  const { t } = useTranslation();
  const jobDescription = useInterviewStore((state) => state.jobDescription);
  const interviewConfig = useInterviewStore((state) => state.interviewConfig);
  const setCurrentInterview = useInterviewStore((state) => state.setCurrentInterview);

  if (!jobDescription) return null;

  const startInterview = async () => {
    try {
      // Generate the initial greeting
      const greeting = await generateInterviewQuestion(
        jobDescription,
        {
          ...interviewConfig,
          isInitialGreeting: true
        },
        [],
        []
      );

      // Create the interview
      const interview = await createInterview(jobDescription.title, {
        ...interviewConfig,
        jobDescription
      });

      // Set the current interview with the initial question
      setCurrentInterview({
        id: interview.id,
        jobTitle: jobDescription.title,
        candidateName: 'Interviewer',
        date: new Date().toISOString(),
        status: 'in-progress',
        responses: [],
        currentQuestion: greeting
      });

      toast.success(t('interview.management.created'));
    } catch (error) {
      console.error('Error creating interview:', error);
      toast.error(t('errors.creatingInterview'));
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <FileTextIcon className="h-6 w-6 text-indigo-600" />
          {jobDescription.title}
        </h2>
        <p className="mt-4 text-gray-600">{jobDescription.description}</p>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <ListIcon className="h-5 w-5 text-indigo-600" />
          {t('jobDescription.requirements')}
        </h3>
        <ul className="list-disc pl-5 space-y-2">
          {jobDescription.requirements.map((req, index) => (
            <li key={index} className="text-gray-600">
              {req}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <CheckSquareIcon className="h-5 w-5 text-indigo-600" />
          {t('jobDescription.responsibilities')}
        </h3>
        <ul className="list-disc pl-5 space-y-2">
          {jobDescription.responsibilities.map((resp, index) => (
            <li key={index} className="text-gray-600">
              {resp}
            </li>
          ))}
        </ul>
      </div>

      <button
        onClick={startInterview}
        className="w-full mt-6 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        {t('interview.controls.startRecording')}
      </button>
    </div>
  );
}